/**
 * UnchangedBlock — focus-mode wrapper for paragraphs that *do not* belong
 * to a change record (M3 Phase 3.5).
 *
 * Visual contract:
 *
 *   enabled === false            → opacity-100, no interaction
 *   enabled && !dimmed           → opacity-30,  click-to-light-up
 *   enabled && dimmed            → opacity-100  for 2 s, then dims
 *
 * Always uses `transition-opacity duration-normal` so the fade in/out matches
 * the rest of the workspace (250 ms — see `transition.duration.normal`).
 *
 * Why the wrapper instead of a global stylesheet:
 *
 *   1. Change blocks are rendered inside `DecidableChange`, which already
 *      owns their hover state, ring, and decision badges. We must NOT touch
 *      their opacity in focus mode — change-anchored content stays at 100 %
 *      so reviewers always see the legal-substance diff clearly.
 *   2. A scoped wrapper keeps the per-row opacity decision a render-time
 *      derivative of `enabled`, making it trivial to reason about and unit
 *      test (no global selectors to grep for).
 *
 * Why precomputed props (T3 — perf hardening):
 *
 *   Pre-T3 this component received the full `UseFocusReadModeResult` and
 *   derived `tempActive = focus.isTempActive(nodeKey)` internally. Because
 *   `isTempActive` is a `useCallback` keyed on `activeKeys`, its identity
 *   flips every time a sibling UnchangedBlock is lit up. That cascaded to
 *   every UnchangedBlock re-rendering on every tempActivate. The parent
 *   now precomputes `dimmed: boolean` and passes it down, so React.memo
 *   with default shallow compare can skip the row entirely when nothing
 *   actually changed.
 *
 *   `tempActivate` is the stable callback from `useFocusReadMode` (its
 *   `useCallback` deps are `[]`). The component calls `tempActivate(nodeKey)`
 *   on click — no inline closure needed at the call site, so the bound
 *   identity stays stable across renders.
 */
import { memo, type ReactNode } from 'react';

interface UnchangedBlockProps {
  /** Stable per-paragraph React key — drives `tempActivate(nodeKey)`. */
  nodeKey: string;
  /** Whether focus mode is globally on. When false, renders plainly. */
  enabled: boolean;
  /** Whether this row is currently temp-lit. `enabled && !dimmed` is the
   *  normal dimmed state; `enabled && dimmed` is the lit state. */
  dimmed: boolean;
  /** Stable `tempActivate(key)` callback from `useFocusReadMode`. When
   *  undefined (e.g. inside a Storybook fixture), clicks are no-ops. */
  tempActivate?: (key: string) => void;
  children: ReactNode;
}

export const UnchangedBlock = memo(function UnchangedBlock({
  nodeKey,
  enabled,
  dimmed,
  tempActivate,
  children,
}: UnchangedBlockProps): React.JSX.Element {
  // Without focus mode ON we render a no-op transparent div so we don't
  // introduce extra event listeners or DOM attributes for nothing.
  if (!enabled) {
    return <div data-unchanged-key={nodeKey}>{children}</div>;
  }

  const handleClick = (): void => {
    tempActivate?.(nodeKey);
  };

  // Use Tailwind utilities for the lit/dim state so the JIT keeps both
  // class chains warm. `opacity-30` reads from `tailwind.config.ts ->
  // theme.extend.opacity.30 = '0.30'` (single source of truth, not a
  // hardcoded literal).
  const opacityClass = dimmed ? 'opacity-100' : 'opacity-30';

  return (
    <div
      data-unchanged-key={nodeKey}
      data-temp-focus={dimmed ? 'true' : undefined}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          handleClick();
        }
      }}
      title="点击展开 2 秒"
      className={`cursor-pointer transition-opacity duration-normal ease-out ${opacityClass}`}
    >
      {children}
    </div>
  );
});

export default UnchangedBlock;
